import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useQuery } from '@tanstack/react-query';
import { categoryService } from '../../lib/api/shopService';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CATEGORY_PLACEHOLDER_IMAGE } from '../../utils/constants';

const PopularCategories = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoryService.getCategories(),
    enabled: true,
  });

  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    variableWidth: true,
    slidesToScroll: 2,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

  return (
    <section className="py-8">
      {categoriesData?.data?.length > 0 && (
        <div className="container mx-auto px-8 lg:px-4">
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {t('home.popular_categories.title')}
            </h2>
          </div>

          {/* Categories Carousel */}
          <div className="relative">
            <style jsx>{`
              .categories-slider .slick-slide {
                width: 10.5rem !important;
                padding: 0 6px;
              }
              .categories-slider .slick-list {
                margin: 0 -6px;
                overflow: hidden;
              }
              .categories-slider .slick-track {
                display: flex !important;
                align-items: flex-start;
              }
              .categories-slider .slick-slide > div {
                height: 100%;
              }
              .categories-slider .slick-arrow {
                border-radius: 50%;
                box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                z-index: 10;
              }
              .categories-slider .slick-arrow:hover {
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
              }
              .categories-slider .slick-arrow:before {
                display: none;
              }
              .categories-slider .slick-prev {
                left: -20px;
              }
              .categories-slider .slick-next {
                right: -30px;
              }
            `}</style>

            <Slider {...settings} className="categories-slider">
              {categoriesData?.data?.map((category) => (
                <div
                  key={category._id}
                  title={category?.name}
                  className="flex w-40 cursor-pointer flex-col items-center"
                  onClick={() => navigate(`/categories`, { state: { categoryId: category._id } })}
                >
                  <div className="w-full rounded-xl border border-gray-200 shadow-sm transition-shadow hover:shadow-md">
                    <img
                      src={category?.image_url || CATEGORY_PLACEHOLDER_IMAGE}
                      alt={category?.name}
                      className="w-full h-32 object-cover rounded-xl"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = CATEGORY_PLACEHOLDER_IMAGE;
                      }}
                    />
                  </div>
                  <p className="mt-3 text-sm font-medium text-gray-700 text-center w-full truncate px-1">
                    {category.name}
                  </p>
                </div>
              ))}
            </Slider>
          </div>
        </div>
      )}
    </section>
  );
};

// Custom Arrow Components
const NextArrow = ({ className, style, onClick }) => (
  <button
    className={`${className} absolute -right-12 top-1/2 transform -translate-y-1/2 bg-red-600 rounded-full p-2 shadow-md hover:shadow-lg transition-shadow z-10`}
    style={{ ...style }}
    onClick={onClick}
  >
    <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  </button>
);

const PrevArrow = ({ className, style, onClick }) => (
  <button
    className={`${className} absolute -left-12 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-md hover:shadow-lg transition-shadow z-10`}
    style={{ ...style }}
    onClick={onClick}
  >
    <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  </button>
);

export default PopularCategories;